import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
//
import LightboxModal from 'src/components/LightboxModal';
import Scrollbar from 'src/components/Scrollbar';
import ChatMessageItemSignWise from './ChatMessageItemSignWise';
import { WA } from '../whatsappTheme';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import { isHelpDeskJoinMessage } from '../helpDeskSession'

const WAITING_INITIAL_DELAY_MS = 3000
const WAITING_TYPING_MS = 1800
const WAITING_BETWEEN_MESSAGES_MS = 1200

/** Avoid replaying bot waiting animation after close/reopen for the same session */
const completedWaitingAnimations = new Set()

const downloadAttachmentInPlace = async (url, fileName = 'document') => {
  if (!url) return
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error('Download failed')
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = fileName || 'document'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  } catch (error) {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName || 'document'
    link.rel = 'noopener'
    link.target = '_blank'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
}

const getOfficePreviewUrl = (url = '') => {
  if (!url) return ''
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
}

const getMessageTimestamp = (message) => {
  const raw = message?.created_at
  if (raw == null || raw === 'Invalid Date') return 0
  const num = Number(raw)
  if (!Number.isNaN(num) && num > 0) return num < 1e12 ? num : Math.floor(num / 1000)
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000)
}

const buildHelpDeskStatusNoticeMessage = (
  notice,
  waitingAgentName,
  waitingAgentPic,
  role = 'customer',
) => {
  const isAgent = role === 'agent'
  if (notice?.type === 'offline') {
    return {
      id: notice.id || 'help-desk-offline-during-chat',
      sender: waitingAgentName || 'Help Desk',
      text: isAgent
        ? 'Help Desk is now offline. The customer can still leave messages.'
        : 'Help Desk is currently offline. You can continue to leave messages — an agent will reply when they are back online.',
      type: 'received',
      contentType: 'bot',
      profile_pic: waitingAgentPic || null,
      created_at: notice.created_at || Math.floor(Date.now() / 1000),
    }
  }
  if (notice?.type === 'online') {
    return {
      id: notice.id || 'help-desk-online-during-chat',
      sender: waitingAgentName || 'Help Desk',
      text: isAgent
        ? 'Help Desk is back online.'
        : 'Help Desk is back online. An agent is available to continue your chat.',
      type: 'received',
      contentType: 'bot',
      profile_pic: waitingAgentPic || null,
      created_at: notice.created_at || Math.floor(Date.now() / 1000),
    }
  }
  return null
}

const insertMessagesByTimestamp = (messages, inserts) => {
  if (!inserts.length) return messages
  let merged = [...messages]
  inserts.forEach((insert) => {
    const ts = getMessageTimestamp(insert)
    let insertAt = merged.length
    for (let i = 0; i < merged.length; i += 1) {
      if (getMessageTimestamp(merged[i]) > ts) {
        insertAt = i
        break
      }
    }
    merged = [...merged.slice(0, insertAt), insert, ...merged.slice(insertAt)]
  })
  return merged
}

export default function ChatMessageListSignWise({
  messages,
  setMessages,
  isTyping,
  typingUser,
  waitingForAgent = false,
  agentJoined = false,
  waitingAgentName = 'Help Desk',
  waitingAgentPic = null,
  agentOnline = true,
  keepOfflineWaiting = false,
  keepOnlineWaiting = false,
  helpDeskStatusNotices = [],
  helpDeskStatusNoticeRole = 'customer',
  participants = [],
  recipientOnline = false,
  chatRating = null,
  showEndRating = false,
  onReply = null,
  onJoinChat = null,
  joiningChat = false,
  isGroupChat = false,
}) {
  const scrollRef = useRef();
  const stickToBottomRef = useRef(true)
  const lastMessageIdRef = useRef(null)
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [waitingReplyStep, setWaitingReplyStep] = useState(0)
  let user = JSON.parse(localStorage.getItem('user'))

  const handleScroll = () => {
    const node = scrollRef.current
    if (!node) return
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight
    stickToBottomRef.current = distanceFromBottom < 80
  }

  useEffect(() => {
    const list = Array.isArray(messages) ? messages.filter((item) => !item?.isTyping) : []
    const last = list[list.length - 1]
    const lastId = last?.id ?? null
    const isOwnNew = last?.type === 'sent' && lastId !== lastMessageIdRef.current
    lastMessageIdRef.current = lastId
    if (!stickToBottomRef.current && !isOwnNew) return
    stickToBottomRef.current = true
    const node = scrollRef.current
    if (node) {
      node.scrollTop = node.scrollHeight
    }
  }, [messages, waitingForAgent, agentOnline, waitingReplyStep, isTyping, typingUser, helpDeskStatusNotices]);

  const list = Array.isArray(messages) ? messages : []
  const images = list
    .filter((item) => {
      if (!item?.attachment) return false
      if (item?.contentType === 'image') return true
      return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(item.attachment))
    })
    .map((item) => item?.attachment)

  const handleOpenLightbox = (url) => {
    setSelectedImage(images.indexOf(url))
    setOpenLightbox(true)
  };

  const handleOpenAttachmentPreview = (file) => {
    if (!file?.url) return
    if (file.kind === 'image') {
      handleOpenLightbox(file.url)
      return
    }
    setAttachmentPreview(file)
  }
  let lastEndIndex = -1
  list.forEach((item, index) => {
    if (item?.contentType === 'end' || item?.message_type === 'end') lastEndIndex = index
  })
  // Keep waiting bot messages before the join event after an agent joins
  let joinIndex = -1
  if (agentJoined) {
    list.forEach((item, index) => {
      if (index > lastEndIndex && joinIndex < 0 && isHelpDeskJoinMessage(item)) {
        joinIndex = index
      }
    })
  }
  const persistWaiting = keepOnlineWaiting || keepOfflineWaiting
  const activelyWaiting = waitingForAgent && !agentJoined
  let insertWaitingAfter = -1
  list.forEach((item, index) => {
    if (index <= lastEndIndex) return
    if (joinIndex >= 0 && index >= joinIndex) return
    if (
      insertWaitingAfter < 0 &&
      (activelyWaiting || persistWaiting) &&
      item.type === 'sent' &&
      !item.isTyping &&
      item.contentType !== 'system' &&
      item.contentType !== 'end' &&
      item.contentType !== 'bot'
    ) {
      insertWaitingAfter = index
    }
  })

  const waitingAnchorId =
    insertWaitingAfter >= 0 ? list[insertWaitingAfter]?.id ?? insertWaitingAfter : null
  const showWaitingReplies = insertWaitingAfter >= 0 && (activelyWaiting || persistWaiting)
  // Offline: only the offline notice. Online: keep that notice (if shown) and add the other two.
  const showOfflineReply =
    showWaitingReplies && (keepOfflineWaiting || (!agentOnline && activelyWaiting))
  const showOnlineReplies =
    showWaitingReplies && (keepOnlineWaiting || (agentOnline && activelyWaiting))
  const totalWaitingReplies =
    (showOfflineReply ? 1 : 0) + (showOnlineReplies ? 2 : 0)
  const waitingCompletionKey =
    showWaitingReplies && waitingAnchorId != null
      ? `${waitingAnchorId}:${totalWaitingReplies}:${keepOfflineWaiting}:${keepOnlineWaiting}`
      : null

  const waitingReplyStepRef = useRef(0)
  waitingReplyStepRef.current = waitingReplyStep

  useEffect(() => {
    if (!waitingCompletionKey) return undefined
    if (waitingReplyStep >= totalWaitingReplies + 1 && totalWaitingReplies > 0) {
      completedWaitingAnimations.add(waitingCompletionKey)
    }
    return undefined
  }, [waitingReplyStep, waitingCompletionKey, totalWaitingReplies])

  useEffect(() => {
    if (!showWaitingReplies || totalWaitingReplies === 0) {
      setWaitingReplyStep(0)
      return undefined
    }

    if (waitingCompletionKey && completedWaitingAnimations.has(waitingCompletionKey)) {
      setWaitingReplyStep(totalWaitingReplies + 1)
      return undefined
    }

    const prev = waitingReplyStepRef.current
    const shown = Math.max(0, prev - 1)
    const timers = []

    // Going offline (fewer replies): restart with only the offline message.
    if (shown > totalWaitingReplies) {
      setWaitingReplyStep(0)
      timers.push(window.setTimeout(() => setWaitingReplyStep(1), WAITING_INITIAL_DELAY_MS))
      timers.push(
        window.setTimeout(
          () => setWaitingReplyStep(2),
          WAITING_INITIAL_DELAY_MS + WAITING_TYPING_MS,
        ),
      )
      return () => timers.forEach((timer) => window.clearTimeout(timer))
    }

    // Already showing everything needed.
    if (shown >= totalWaitingReplies && prev >= 2) {
      if (waitingCompletionKey) completedWaitingAnimations.add(waitingCompletionKey)
      return undefined
    }

    // Offline → online: keep existing messages, append the new ones (do not replace).
    if (prev >= 2 && shown < totalWaitingReplies) {
      let at = WAITING_BETWEEN_MESSAGES_MS
      for (let count = shown + 1; count <= totalWaitingReplies; count += 1) {
        const step = count + 1
        timers.push(window.setTimeout(() => setWaitingReplyStep(step), at))
        at += WAITING_BETWEEN_MESSAGES_MS
      }
      return () => timers.forEach((timer) => window.clearTimeout(timer))
    }

    // Fresh waiting sequence.
    setWaitingReplyStep(0)
    timers.push(window.setTimeout(() => setWaitingReplyStep(1), WAITING_INITIAL_DELAY_MS))
    let at = WAITING_INITIAL_DELAY_MS + WAITING_TYPING_MS
    for (let index = 0; index < totalWaitingReplies; index += 1) {
      timers.push(window.setTimeout(() => setWaitingReplyStep(index + 2), at))
      at += WAITING_BETWEEN_MESSAGES_MS
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [
    showWaitingReplies,
    totalWaitingReplies,
    waitingAnchorId,
    agentOnline,
    keepOfflineWaiting,
    keepOnlineWaiting,
    waitingCompletionKey,
  ])

  const hasVisibleContent = (message) => {
    if (message?.isTyping) return true
    if (message?.contentType === 'system' || message?.message_type === 'system') return true
    if (message?.contentType === 'end' || message?.message_type === 'end') return true
    if (message?.contentType === 'image' && message?.attachment) return true
    if (message?.contentType === 'file' && message?.attachment) return true
    if (message?.attachment) return true
    const text = String(message?.text || '')
      .replace(/<(.|\n)*?>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim()
    return text.length > 0
  }

  let displayMessages = []
  list.forEach((message, index) => {
    if (!hasVisibleContent(message)) return
    displayMessages.push(message)
    if (index === insertWaitingAfter) {
      if (waitingReplyStep === 1) {
        displayMessages.push({
          id: 'help-desk-waiting-loading',
          sender: waitingAgentName || 'Help Desk',
          text: 'typing...',
          type: 'received',
          isTyping: true,
          profile_pic: waitingAgentPic || null,
          created_at: message.created_at,
        })
      }

      const waitingReplies = []
      if (showOfflineReply) {
        waitingReplies.push({
          id: 'help-desk-waiting-offline',
          sender: waitingAgentName || 'Help Desk',
          text: 'Help Desk is currently offline. Please leave a message — an agent will join the chat when they come online.',
          type: 'received',
          contentType: 'bot',
          profile_pic: waitingAgentPic || null,
          created_at: message.created_at,
        })
      }
      if (showOnlineReplies) {
        waitingReplies.push({
          id: 'help-desk-waiting-intro',
          sender: waitingAgentName || 'Help Desk',
          text: 'Hi! Thanks for reaching out. We have received your message.',
          type: 'received',
          contentType: 'bot',
          profile_pic: waitingAgentPic || null,
          created_at: message.created_at,
        })
        waitingReplies.push({
          id: 'help-desk-waiting-soon',
          sender: waitingAgentName || 'Help Desk',
          text: 'One of our Help Desk agents will be with you soon. Stay tuned!',
          type: 'received',
          contentType: 'bot',
          profile_pic: waitingAgentPic || null,
          created_at: message.created_at,
        })
      }

      const visibleReplyCount =
        agentJoined && persistWaiting
          ? waitingReplies.length
          : Math.max(0, waitingReplyStep - 1)
      waitingReplies.slice(0, visibleReplyCount).forEach((reply) => {
        displayMessages.push(reply)
      })
    }
  })

  // Live peer typing indicator ("...") — always append when someone else is typing
  if (agentJoined && Array.isArray(helpDeskStatusNotices) && helpDeskStatusNotices.length > 0) {
    const statusMessages = helpDeskStatusNotices
      .map((notice) =>
        buildHelpDeskStatusNoticeMessage(
          notice,
          waitingAgentName,
          waitingAgentPic,
          helpDeskStatusNoticeRole,
        ),
      )
      .filter(Boolean)
    displayMessages = insertMessagesByTimestamp(displayMessages, statusMessages)
  }
  const showLiveTyping =
    Boolean(isTyping) &&
    typingUser &&
    typingUser?.user_id != null &&
    String(user?.user_id) !== String(typingUser.user_id) &&
    !displayMessages.some((item) => String(item?.id || '').startsWith('typing-'))
  if (showLiveTyping) {
    displayMessages.push({
      id: `typing-${typingUser.user_id}`,
      sender: typingUser?.first_name || typingUser?.full_name || 'Help Desk',
      text: 'typing...',
      type: 'received',
      isTyping: true,
      profile_pic: typingUser?.profile_pic || null,
    })
  }

  return (
    <Scrollbar
        scrollableNodeProps={{ ref: scrollRef, onScroll: handleScroll }}
      sx={{
        p: 2.5,
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
        backgroundColor: WA.chatBg,
        overflowX: 'hidden',
        '& .simplebar-content-wrapper': {
          height: '100%',
          overflowX: 'hidden !important',
          overflowY: 'auto !important',
        },
        '& .simplebar-content': {
          maxWidth: '100%',
          overflowX: 'hidden',
        },
        '& .simplebar-track.simplebar-horizontal': {
          display: 'none',
        },
      }}
    >
      {(() => {
        let lastEndIndex = -1
        displayMessages.forEach((item, idx) => {
          if (item?.contentType === 'end' || item?.message_type === 'end') lastEndIndex = idx
        })
        return displayMessages.map((message, index) => (
          <ChatMessageItemSignWise
            key={message.id}
            message={message}
            onOpenLightbox={handleOpenLightbox}
            onOpenAttachmentPreview={handleOpenAttachmentPreview}
            participants={participants}
            recipientOnline={recipientOnline}
            chatRating={chatRating}
            showEndRating={Boolean(showEndRating && index === lastEndIndex)}
            onReply={onReply}
            onJoinChat={onJoinChat}
            joiningChat={joiningChat}
            isGroupChat={isGroupChat}
          />
        ))
      })()}

      <LightboxModal
        images={images}
        photoIndex={selectedImage}
        setPhotoIndex={setSelectedImage}
        isOpen={openLightbox}
        onClose={() => setOpenLightbox(false)}
      />

      <Dialog
        open={Boolean(attachmentPreview)}
        onClose={() => setAttachmentPreview(null)}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1600 }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            maxHeight: '90vh',
            position: 'relative',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setAttachmentPreview(null)}
          aria-label="Close preview"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.92)',
            border: `1px solid ${WA.border}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            pr: 6,
            py: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ flexGrow: 1, fontWeight: 700, minWidth: 0 }}
            title={attachmentPreview?.name}
          >
            {attachmentPreview?.name || 'Attachment preview'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#111', minHeight: 360 }}>
          {attachmentPreview?.kind === 'video' ? (
            <Box
              component="video"
              src={attachmentPreview.url}
              controls
              autoPlay
              sx={{ display: 'block', width: '100%', maxHeight: '70vh', bgcolor: '#000' }}
            />
          ) : attachmentPreview?.kind === 'audio' ? (
            <Box sx={{ p: 4, bgcolor: '#fff', display: 'flex', justifyContent: 'center' }}>
              <Box component="audio" src={attachmentPreview.url} controls autoPlay sx={{ width: '100%' }} />
            </Box>
          ) : attachmentPreview?.kind === 'pdf' || attachmentPreview?.kind === 'text' ? (
            <Box
              component="iframe"
              title={attachmentPreview.name || 'File preview'}
              src={attachmentPreview.url}
              sx={{ border: 0, width: '100%', height: '70vh', bgcolor: '#fff' }}
            />
          ) : attachmentPreview?.kind === 'office' ? (
            <Box
              component="iframe"
              title={attachmentPreview.name || 'Office preview'}
              src={getOfficePreviewUrl(attachmentPreview.url)}
              sx={{ border: 0, width: '100%', height: '70vh', bgcolor: '#fff' }}
            />
          ) : (
            <Box
              sx={{
                minHeight: 360,
                height: '70vh',
                bgcolor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: WA.text }}>
                Preview is not available for this file type
              </Typography>
              <Typography variant="body2" sx={{ color: WA.meta, maxWidth: 420 }}>
                Use Download or Open in new tab to view {attachmentPreview?.name || 'this file'}.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button
            startIcon={<GetAppIcon />}
            onClick={() =>
              downloadAttachmentInPlace(attachmentPreview?.url, attachmentPreview?.name)
            }
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Download
          </Button>
          <Button
            onClick={() => {
              if (!attachmentPreview?.url) return
              const target =
                attachmentPreview.kind === 'office'
                  ? getOfficePreviewUrl(attachmentPreview.url)
                  : attachmentPreview.url
              window.open(target, '_blank', 'noopener,noreferrer')
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Open in new tab
          </Button>
        </DialogActions>
      </Dialog>
    </Scrollbar>
  );
}
